import React, { Component } from "react";
import PropTypes from "prop-types";
import * as R from "ramda";

import { styled } from "@mui/material/styles";
import List from "@mui/material/List";
import ListSubheader from "@mui/material/ListSubheader";

import generateTeacherMaterialGetters from "~/libs/dls";
import ScrollIntoViewIfNeeded from "~/components/ScrollIntoViewIfNeeded";
import { NON_ASSIGNED_PAGE } from "~/constants/materials";
import {
  absolutePageToRelative,
  getDeepLinkWindowName,
  isAppleiPadDevice,
  isDeepLink,
  getRelativeDeeplink
} from "~/utils";
import { hasMissingUserLicense } from "~/utils/dlsmaterial";
import PresentationMaterial from "~/components/PresentationMaterial";

import NonAssignedMaterialsList from "./NonAssignedMaterialsList";
import NonAssignedPresentationMaterialsList from "./NonAssignedPresentationMaterialsList";
import DragAndDropEntry from "./DragAndDropEntry";
import Button from "@mui/material/Button";
import { borderRadius, textTransform } from "@mui/system";

const StyledSubheader = styled(ListSubheader)(({ theme, isActive }) => ({
  "&.MuiListSubheader-root": {
    backgroundColor: isActive ? "#66a220" : theme.palette.mbgreen[1],
    color: isActive ? "white" : "#66a220",
    cursor: "pointer",
    lineHeight: "3rem"
  },
  "&.MuiListSubheader-gutters": {
    width: "100%",
    height: "3rem",
    borderRadius: 0,
    textAlign: "start",
    justifyContent: "start",
    textTransform: "none"
  },
  "&:focus-visible": {
    outline: theme.outline(),
    outlineOffset: "-2px"
  }
}));

class TeachingMaterialList extends Component {
  constructor(props) {
    super(props);

    this.onItemClick = this.onItemClick.bind(this);
    this.onHeaderClick = this.onHeaderClick.bind(this);
  }

  // FIXME: material gets only passed by NonAssignedPresentationMaterialsList
  onItemClick({ id, page, renderType, type, material, materialType }) {
    const { showModal, openViewerModal } = this.props;

    return () => {
      if (type === "dlsmaterial") {
        const DLS = generateTeacherMaterialGetters(material);

        if (hasMissingUserLicense(material)) return;

        if (renderType === "docviewer") {
          openViewerModal({
            fullScreen: true,
            additionalProps: { materialId: material._id }
          });
        } else if (renderType === "link") {
          if (isDeepLink(material.displayAction)) {
            const windowName = getDeepLinkWindowName(material.displayAction);

            if (isAppleiPadDevice() && window.openWindows.hasOwnProperty(windowName)) {
              try {
                window.openWindows[windowName].close();
              } catch (e) {}
            }

            window.openWindows[windowName] = window.open(getRelativeDeeplink(material.displayAction), windowName);
          } else {
            showModal("MATERIAL_RENDERER", {
              material: {
                renderType: material.renderType,
                sourcePath: material.displayAction,
                linkText: material.name,
                name: material.name,
                title: material.title
              }
            });
          }
        } else {
          showModal("MATERIAL_RENDERER", {
            material: {
              renderType: material.renderType,
              sourcePath: DLS.getStaticFilePath(material.displayAction),
              linkText: material.name,
              name: material.name,
              title: material.title
            }
          });
        }
      } else if (type === "dlsUserMaterial") {
        showModal("DLS_USER_MATERIAL_SHOW", { material });
      } else if (type === "material") {
        showModal("SHOW_MATERIAL", { id, page, materialType, renderType });
      } else {
        console.error(`Unknown materialType [${type}]`);
      }
    };
  }

  onHeaderClick(page) {
    const { goToPage, isGreaterThanMedium, closeSidebarRight } = this.props;

    return () => {
      goToPage(page);

      if (!isGreaterThanMedium) {
        closeSidebarRight();
      }
    };
  }

  render() {
    const { materials, activePages, activeTab } = this.props;
    const materialsOnPages = R.omit([NON_ASSIGNED_PAGE], materials);

    const renderListHeader = (page, listItems) => {
      if (R.isEmpty(listItems)) return null;
      const parsed = parseInt(page, 10);
      const activeSelfAndAllTabIsActive = activeTab === "all" && R.includes(parsed, activePages);

      return (
        <ScrollIntoViewIfNeeded key={`header_${parsed}`} active={R.includes(parsed, activePages)}>
          <StyledSubheader
            disableSticky
            onClick={this.onHeaderClick(parsed)}
            isActive={activeSelfAndAllTabIsActive}
            component={Button}
          >
            {`Seite ${absolutePageToRelative(parsed)}`}
          </StyledSubheader>
        </ScrollIntoViewIfNeeded>
      );
    };

    const renderListItem = (material, page) => {
      if (!material || !material._id || material.notInSidebar) return null;

      const isPresentationOnly = R.pathOr(false, ["position", "isPresentationOnly"], material);

      if (isPresentationOnly) {
        return (
          <PresentationMaterial
            key={`${material._id}_${page}`}
            material={material}
            page={null}
            onClickHandler={this.onItemClick}
          />
        );
      }

      return (
        <DragAndDropEntry
          activePages={activePages}
          key={`${material._id}_${material.position._id}`}
          material={material}
          onHandleClick={this.onItemClick({
            id: material._id,
            page: material.position.page,
            renderType: material.renderType,
            type: material.type,
            materialType: material.materialType
          })}
        />
      );
    };

    const materialsByPages = R.mapObjIndexed((materials, page) => {
      const listItems = materials.map(material => renderListItem(material, page)).filter(material => !!material);
      return [renderListHeader(page, listItems), ...listItems];
    }, materialsOnPages);

    return (
      <List>
        <NonAssignedMaterialsList onClickHandler={this.onItemClick} />
        <NonAssignedPresentationMaterialsList onClickHandler={this.onItemClick} />
        {R.unnest(R.values(materialsByPages))}
      </List>
    );
  }
}

TeachingMaterialList.propTypes = {
  goToPage: PropTypes.func.isRequired,
  activeTab: PropTypes.string.isRequired,
  activePages: PropTypes.array.isRequired,
  materials: PropTypes.object.isRequired,
  isGreaterThanMedium: PropTypes.bool.isRequired,
  closeSidebarRight: PropTypes.func.isRequired,
  showModal: PropTypes.func.isRequired,
  openViewerModal: PropTypes.func.isRequired
};

TeachingMaterialList.defaultProps = {};

export default TeachingMaterialList;
